import { useEffect, useRef, useState } from 'react';
import {
  useCreateTemplate,
  useDeleteTemplate,
  useTemplate,
  useTemplates,
  useUpdateTemplate,
} from '../lib/templates';
import { apiErrorMessage } from '../lib/api';
import { ConfirmDialog } from '../components/ConfirmDialog';
import type { Template, TemplateInput } from '@stromo/shared';

interface PositionDraft {
  // Stabile UID über die ganze Edit-Session — wird als React-key verwendet.
  // Darf NICHT aus title/position_number abgeleitet werden, sonst remountet
  // React das <input> bei jedem Tastendruck und der Focus geht verloren.
  uid: string;
  sort_order: number;
  position_number: number;
  title: string;
  is_group_header: boolean;
}

const newUid = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `p-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const emptyDraft = (): TemplateDraft => ({
  name: '',
  description: '',
  is_active: true,
  positions: [],
});

interface TemplateDraft {
  name: string;
  description: string;
  is_active: boolean;
  positions: PositionDraft[];
}

function templateToDraft(t: Template): TemplateDraft {
  return {
    name: t.name,
    description: t.description ?? '',
    is_active: t.is_active,
    positions: (t.positions ?? []).map((p, idx) => ({
      uid: newUid(),
      sort_order: idx,
      position_number: p.position_number,
      title: p.title,
      is_group_header: p.is_group_header,
    })),
  };
}

function draftToInput(d: TemplateDraft): TemplateInput {
  return {
    name: d.name.trim(),
    description: d.description.trim() || null,
    is_active: d.is_active,
    positions: d.positions.map((p, idx) => ({
      sort_order: idx,
      position_number: p.position_number,
      title: p.title.trim(),
      is_group_header: p.is_group_header,
    })),
  };
}

export function TemplatesPage() {
  const templates = useTemplates();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isNew, setIsNew] = useState(false);

  useEffect(() => {
    if (!selectedId && !isNew && (templates.data ?? []).length > 0) {
      const first = templates.data?.[0];
      if (first) setSelectedId(first.id);
    }
  }, [templates.data, selectedId, isNew]);

  const detail = useTemplate(isNew ? undefined : selectedId ?? undefined);
  const createMut = useCreateTemplate();
  const updateMut = useUpdateTemplate(selectedId ?? 0);
  const deleteMut = useDeleteTemplate();
  const [deleteTarget, setDeleteTarget] = useState<Template | null>(null);

  const [draft, setDraft] = useState<TemplateDraft>(emptyDraft());
  /** Name der Quelle, solange ein ungespeichertes Duplikat offen ist. */
  const [copiedFrom, setCopiedFrom] = useState<string | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Neue Vorlagen (leer oder Duplikat) setzen ihren Draft selbst — hier
  // nur beim Wechsel auf eine bestehende Vorlage deren Stand laden.
  useEffect(() => {
    if (isNew) return;
    if (detail.data) {
      setDraft(templateToDraft(detail.data));
    }
  }, [detail.data, isNew]);

  const setPos = (idx: number, patch: Partial<PositionDraft>) => {
    setDraft((d) => ({
      ...d,
      positions: d.positions.map((p, i) => (i === idx ? { ...p, ...patch } : p)),
    }));
  };

  const removePos = (idx: number) => {
    setDraft((d) => ({ ...d, positions: d.positions.filter((_, i) => i !== idx) }));
  };

  const addPos = (groupHeader = false) => {
    setDraft((d) => {
      const next = groupHeader
        ? 0
        : d.positions.filter((p) => !p.is_group_header).reduce((m, p) => Math.max(m, p.position_number), 0) + 1;
      return {
        ...d,
        positions: [
          ...d.positions,
          {
            uid: newUid(),
            sort_order: d.positions.length,
            position_number: next,
            title: groupHeader ? 'Neue Gruppe' : 'Neue Position',
            is_group_header: groupHeader,
          },
        ],
      };
    });
  };

  /**
   * Position direkt nach einer Gruppe einfügen — sucht ans Ende der Gruppe
   * (vor dem nächsten Gruppen-Header). So muss der Anwender die neue
   * Position nicht erst manuell aus der Liste-Unten nach oben ziehen.
   */
  const addPosAfterGroup = (groupIdx: number) => {
    setDraft((d) => {
      let insertAt = d.positions.length;
      for (let i = groupIdx + 1; i < d.positions.length; i += 1) {
        const p = d.positions[i];
        if (!p) break;
        if (p.is_group_header) { insertAt = i; break; }
      }
      const nextNum =
        d.positions.filter((p) => !p.is_group_header).reduce((m, p) => Math.max(m, p.position_number), 0) + 1;
      const inserted: PositionDraft = {
        uid: newUid(),
        sort_order: insertAt,
        position_number: nextNum,
        title: 'Neue Position',
        is_group_header: false,
      };
      const next = [...d.positions];
      next.splice(insertAt, 0, inserted);
      return { ...d, positions: next.map((p, idx) => ({ ...p, sort_order: idx })) };
    });
  };

  const move = (from: number, to: number) => {
    if (from === to) return;
    setDraft((d) => {
      const next = [...d.positions];
      const [item] = next.splice(from, 1);
      if (!item) return d;
      next.splice(to, 0, item);
      return { ...d, positions: next.map((p, idx) => ({ ...p, sort_order: idx })) };
    });
  };

  const [dragIdx, setDragIdx] = useState<number | null>(null);

  const handleSave = () => {
    const input = draftToInput(draft);
    if (!input.name) return;
    if (input.positions.length === 0) return;
    if (isNew) {
      createMut.mutate(input, {
        onSuccess: (created) => {
          setIsNew(false);
          setCopiedFrom(null);
          setSelectedId(created.id);
        },
      });
    } else if (selectedId) {
      updateMut.mutate(input);
    }
  };

  const handleNew = () => {
    setIsNew(true);
    setSelectedId(null);
    setCopiedFrom(null);
    setDraft(emptyDraft());
  };

  /** Öffnet eine ungespeicherte Kopie der gewählten Vorlage (gespeicherter
   *  Stand) mit freiem Namen "… (Kopie)" bzw. "… (Kopie 2)" usw. */
  const handleDuplicate = () => {
    const source = detail.data;
    if (!source) return;
    const taken = new Set((templates.data ?? []).map((t) => t.name));
    let name = `${source.name} (Kopie)`;
    for (let n = 2; taken.has(name); n += 1) name = `${source.name} (Kopie ${n})`;
    setIsNew(true);
    setSelectedId(null);
    setCopiedFrom(source.name);
    setDraft({ ...templateToDraft(source), name });
    // Der Knopf sitzt unter der (oft langen) Positionsliste — Fokus ins
    // Namensfeld scrollt nach oben und lädt direkt zum Umbenennen ein.
    requestAnimationFrame(() => {
      nameInputRef.current?.focus();
      nameInputRef.current?.select();
    });
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteMut.mutate(deleteTarget.id, {
      onSuccess: () => {
        setDeleteTarget(null);
        if (deleteTarget.id === selectedId) setSelectedId(null);
      },
    });
  };

  const saving = createMut.isPending || updateMut.isPending;
  const saveError =
    createMut.isError ? apiErrorMessage(createMut.error) :
    updateMut.isError ? apiErrorMessage(updateMut.error) : null;

  return (
    <>
      <header className="topbar">
        <div className="topbar-title">Vorlagen</div>
        <div className="topbar-actions">
          <button type="button" className="btn btn-primary" onClick={handleNew}>
            + Neue Vorlage
          </button>
        </div>
      </header>

      <div className="content templates-grid">
        <div className="card templates-list-card">
          <div className="card-title">Liste</div>
          {templates.isLoading ? (
            <div className="loading">Lade …</div>
          ) : (templates.data ?? []).length === 0 ? (
            <div className="empty-state" style={{ padding: 16 }}><p>Keine Vorlagen vorhanden.</p></div>
          ) : (
            <ul className="templates-list">
              {(templates.data ?? []).map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => { setIsNew(false); setCopiedFrom(null); setSelectedId(t.id); }}
                    className={`templates-list-item${selectedId === t.id && !isNew ? ' is-active' : ''}`}
                  >
                    <div className="templates-list-item__name">{t.name}</div>
                    <div className="templates-list-item__meta">
                      v{t.version} · {(t.positions ?? []).length} Pos.
                      {!t.is_active && ' · inaktiv'}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card">
          {saveError && <div className="login-error">{saveError}</div>}
          {isNew && copiedFrom && (
            <div className="template-copy-hint">
              Kopie von „{copiedFrom}“ — noch nicht gespeichert. Namen anpassen und mit „Vorlage anlegen“ speichern.
            </div>
          )}

          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Name *</label>
              <input
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                placeholder="z.B. Standard EFH"
                ref={nameInputRef}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Aktiv</label>
              <label style={{ fontSize: 13, color: 'var(--color-text)', display: 'flex', alignItems: 'center', gap: 8, textTransform: 'none', letterSpacing: 0 }}>
                <input
                  type="checkbox"
                  checked={draft.is_active}
                  onChange={(e) => setDraft((d) => ({ ...d, is_active: e.target.checked }))}
                  style={{ width: 16, height: 16 }}
                />
                Sichtbar beim Projekt-Anlegen
              </label>
            </div>
            <div className="form-group full">
              <label className="form-label">Beschreibung</label>
              <input
                value={draft.description}
                onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                placeholder="kurze Beschreibung der Vorlage"
              />
            </div>
          </div>

          <div style={{ marginTop: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="card-title" style={{ margin: 0 }}>Positionen ({draft.positions.length})</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => addPos(false)}>
                + Position
              </button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => addPos(true)}>
                + Gruppe
              </button>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            {draft.positions.length === 0 && (
              <div className="empty-state" style={{ padding: 24 }}><p>Noch keine Positionen.</p></div>
            )}
            {draft.positions.map((p, idx) => (
              <div
                key={p.uid}
                className={`template-pos-row${p.is_group_header ? ' is-group' : ''}${dragIdx === idx ? ' is-dragging' : ''}`}
                draggable
                onDragStart={() => setDragIdx(idx)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => {
                  if (dragIdx != null) move(dragIdx, idx);
                  setDragIdx(null);
                }}
                onDragEnd={() => setDragIdx(null)}
              >
                <span className="template-pos-row__drag" aria-hidden="true">⋮⋮</span>
                {!p.is_group_header && (
                  <input
                    type="number"
                    min={0}
                    value={p.position_number}
                    onChange={(e) => setPos(idx, { position_number: Number(e.target.value) })}
                    className="template-pos-row__num"
                    aria-label="Positionsnummer"
                  />
                )}
                <input
                  value={p.title}
                  onChange={(e) => setPos(idx, { title: e.target.value })}
                  className={`template-pos-row__title${p.is_group_header ? ' is-group' : ''}`}
                  placeholder={p.is_group_header ? 'Gruppen-Bezeichnung' : 'Position'}
                />
                {p.is_group_header && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => addPosAfterGroup(idx)}
                    title="Position direkt unter dieser Gruppe einfügen"
                    style={{ flexShrink: 0 }}
                  >
                    + Position
                  </button>
                )}
                <button
                  type="button"
                  className="template-pos-row__remove"
                  onClick={() => removePos(idx)}
                  aria-label="Position entfernen"
                  title="Entfernen"
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 22, paddingTop: 16, borderTop: '1px solid var(--color-border-soft)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            {!isNew && selectedId && detail.data && (
              <>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => detail.data && setDeleteTarget(detail.data)}
                >
                  Löschen
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleDuplicate}
                  title="Ungespeicherte Kopie dieser Vorlage öffnen"
                >
                  Duplizieren
                </button>
              </>
            )}
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving || !draft.name.trim() || draft.positions.length === 0}
            >
              {saving ? 'Speichere …' : isNew ? 'Vorlage anlegen' : 'Speichern (Version +1)'}
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Vorlage löschen"
        danger
        message={
          deleteTarget
            ? `Vorlage "${deleteTarget.name}" endgültig löschen? Geht nur, wenn keine Projekte sie noch nutzen.`
            : ''
        }
        confirmLabel="Endgültig löschen"
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </>
  );
}
